import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth.jsx";
import { formatApiErr, api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import BrandMark from "@/components/BrandMark";
import { track } from "@/lib/analytics";
import { usePlatformConfig } from "@/lib/platformConfig";
import { toast } from "sonner";
import { MailPlus, Lock, Eye, EyeOff } from "lucide-react";

// Simple password strength scoring: 0 (empty) → 4 (strong)
function scorePassword(pw) {
  if (!pw) return 0;
  let score = 0;
  if (pw.length >= 8) score += 1;
  if (pw.length >= 12) score += 1;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score += 1;
  if (/\d/.test(pw) && /[^A-Za-z0-9]/.test(pw)) score += 1;
  return Math.min(score, 4);
}

const STRENGTH_META = [
  { label: "", color: "" },
  { label: "Weak", color: "bg-red-500" },
  { label: "Fair", color: "bg-orange-400" },
  { label: "Good", color: "bg-yellow-500" },
  { label: "Strong", color: "bg-emerald-500" },
];

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const planIntent = sp.get("plan") || "free";
  const config = usePlatformConfig();

  const [form, setForm] = useState({ name: "", email: "", password: "", org_name: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const pwScore = useMemo(() => scorePassword(form.password), [form.password]);
  const pwMeta = STRENGTH_META[pwScore];

  useEffect(() => {
    document.title = config?.public_signup_enabled
      ? "TOVRA — Create your workspace"
      : "TOVRA — Request early access";
    track("signup_started", { plan_intent: planIntent });
    if (planIntent === "team") {
      try { localStorage.setItem("tovra_plan_intent", "team"); }
      catch (err2) { if (process.env.NODE_ENV === "development") console.debug("[signup] plan_intent write failed", err2); }
    }
  }, [planIntent, config]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await register(form);
      track("workspace_created", { plan_intent: planIntent });
      nav("/onboarding");
    } catch (e2) {
      setErr(formatApiErr(e2.response?.data?.detail) || "Register failed");
    } finally { setBusy(false); }
  };

  // While config is loading, don't render a form (avoid a flash of the public form).
  if (!config) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg-app)]">
        <div className="text-sm text-zinc-500">Loading…</div>
      </div>
    );
  }

  if (!config.public_signup_enabled) {
    return <PrivateBetaGate planIntent={planIntent} />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--bg-app)] p-6">
      <form onSubmit={submit} className="w-full max-w-md bg-white border rounded-xl p-8" data-testid="register-form">
        <BrandMark theme="light" />
        <h2 className="text-3xl font-semibold mt-6 mb-1">Create your workspace</h2>
        <p className="text-sm text-zinc-500 mb-2">Create your own TOVRA workspace in a minute.</p>
        {planIntent === "team" && (
          <p className="text-xs rounded-md bg-orange-50 border border-orange-200 text-orange-900 px-2 py-1.5 mb-4" data-testid="plan-intent-notice">
            You selected the <b>Team</b> plan. Team activation opens up once billing lands — start on Free today.
          </p>
        )}
        <div className="space-y-3">
          <div><Label>Full name</Label><Input data-testid="reg-name" value={form.name} onChange={upd("name")} required /></div>
          <div><Label>Work email</Label><Input data-testid="reg-email" type="email" value={form.email} onChange={upd("email")} required /></div>
          <div>
            <Label htmlFor="reg-password">Password</Label>
            <div className="relative mt-1">
              <Input
                id="reg-password"
                data-testid="reg-password"
                type={showPassword ? "text" : "password"}
                value={form.password}
                onChange={upd("password")}
                required
                className="pr-11"
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                aria-pressed={showPassword}
                data-testid="reg-toggle-password"
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-zinc-400 hover:text-zinc-700"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}
              </button>
            </div>
            {form.password && (
              <div className="mt-2" data-testid="reg-password-strength">
                <div className="flex gap-1" aria-hidden="true">
                  {[1, 2, 3, 4].map((i) => (
                    <div
                      key={i}
                      className={`h-1 flex-1 rounded-full transition-colors ${i <= pwScore ? pwMeta.color : "bg-zinc-200"}`}
                    />
                  ))}
                </div>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-xs text-zinc-500" data-testid="reg-password-strength-label">
                    Strength: <b className="text-zinc-700">{pwMeta.label || "Too short"}</b>
                  </span>
                  {pwScore < 3 && (
                    <span className="text-[11px] text-zinc-400">Use 12+ chars, mix cases &amp; symbols</span>
                  )}
                </div>
              </div>
            )}
          </div>
          <div><Label>Organization name</Label><Input data-testid="reg-org" value={form.org_name} onChange={upd("org_name")} required /></div>
          {err && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{err}</div>}
          <Button data-testid="reg-submit" disabled={busy} type="submit" className="w-full h-11 bg-[var(--brand-primary)] text-white">
            {busy ? "Creating…" : "Create workspace"}
          </Button>
        </div>
        <div className="text-sm mt-4 text-center">
          Already have an account? <Link to="/login" className="text-[var(--brand-accent)]">Sign in</Link>
        </div>
      </form>
    </div>
  );
}

function PrivateBetaGate({ planIntent }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [teamSize, setTeamSize] = useState("");
  const [intendedUse, setIntendedUse] = useState("");
  const [note, setNote] = useState(planIntent === "team" ? "Interested in the Team plan." : "");
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/platform/waitlist", {
        email, name, company, team_size: teamSize, intended_use: intendedUse, note,
      });
      track("request_early_access_clicked", { plan_intent: planIntent });
      setSubmitted(true);
      toast.success("Thanks — we'll be in touch.");
    } catch (e2) {
      toast.error(formatApiErr(e2.response?.data?.detail) || "Could not submit. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--bg-app)] p-6">
      <div className="w-full max-w-md bg-white border rounded-xl p-8" data-testid="private-beta-gate">
        <BrandMark theme="light" />
        <div className="mt-6 flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-[var(--brand-accent)]">
          <Lock className="w-3.5 h-3.5" /> Private beta
        </div>
        <h2 className="text-3xl font-semibold mt-2 mb-2" data-testid="private-beta-headline">
          TOVRA is currently available through private beta access.
        </h2>
        <p className="text-sm text-zinc-600">
          Public signup is temporarily closed while we finish onboarding early customers. Leave your email
          and we&apos;ll get in touch when a spot opens up.
        </p>

        {submitted ? (
          <div className="mt-6 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-900 p-4 text-sm" data-testid="waitlist-thanks">
            You&apos;re on the list. We&apos;ll email <b>{email}</b> as soon as we can invite your team.
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-3" data-testid="waitlist-form">
            <div>
              <Label>Work email</Label>
              <Input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="waitlist-email" />
            </div>
            <div>
              <Label>Your name (optional)</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="waitlist-name" />
            </div>
            <div>
              <Label>Company (optional)</Label>
              <Input value={company} onChange={(e) => setCompany(e.target.value)} data-testid="waitlist-company" />
            </div>
            <div>
              <Label>Team size (optional)</Label>
              <Input value={teamSize} onChange={(e) => setTeamSize(e.target.value)} placeholder="e.g. 10–25" data-testid="waitlist-team-size" />
            </div>
            <div>
              <Label>How do you plan to use TOVRA? (optional)</Label>
              <Input value={intendedUse} onChange={(e) => setIntendedUse(e.target.value)} placeholder="Team communication, projects, virtual office…" data-testid="waitlist-use" />
            </div>
            <div>
              <Label>Anything to add? (optional)</Label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                maxLength={500}
                className="w-full min-h-[70px] rounded-md border border-zinc-300 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-accent)]"
                placeholder="Current tools, use case…"
                data-testid="waitlist-note"
              />
            </div>
            <Button
              type="submit"
              disabled={busy}
              className="w-full h-11 bg-[var(--brand-primary)] text-white"
              data-testid="waitlist-submit"
            >
              <MailPlus className="w-4 h-4 mr-1.5" /> {busy ? "Requesting…" : "Request early access"}
            </Button>
          </form>
        )}

        <div className="text-sm mt-6 text-center text-zinc-600">
          Already have a TOVRA account? <Link to="/login" className="text-[var(--brand-accent)]">Sign in</Link>
        </div>
        <div className="text-xs text-zinc-400 mt-4 text-center">
          <Link to="/" className="hover:text-zinc-600">← Back to tovrawork.com</Link>
        </div>
      </div>
    </div>
  );
}
